import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { defaultSiteDoc } from "../data/content";
import {
  ArrowDown, ArrowUp, Eye, EyeOff, Film, Image as ImageIcon, Loader2, LogOut,
  Music, Plus, Save, Trash2, Upload, X, Lock,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full bg-white/5 border border-white/10 focus:border-gold/60 rounded-md px-3 py-2 text-sm text-ivory outline-none transition-colors duration-300";
const labelCls = "block text-[10px] tracking-[0.2em] uppercase text-smoke mb-1.5";
const cardCls = "border border-white/10 bg-coal/70 rounded-lg p-5";

const Field = ({ label, value, onChange, textarea, placeholder, testid, rows = 3 }) => (
  <div>
    <label className={labelCls}>{label}</label>
    {textarea ? (
      <textarea
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        rows={rows}
        className={inputCls + " resize-y leading-relaxed"}
        data-testid={testid}
      />
    ) : (
      <input
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className={inputCls}
        data-testid={testid}
      />
    )}
  </div>
);

const PickButton = ({ onPick, kind }) => (
  <button
    type="button"
    onClick={() => onPick(kind)}
    className="shrink-0 inline-flex items-center gap-2 border border-gold/40 hover:border-gold text-gold-light text-[10px] tracking-[0.18em] uppercase px-4 py-2 rounded-md transition-colors duration-300"
    data-testid="media-pick-button"
  >
    <ImageIcon size={12} /> Library
  </button>
);

// ------------------------------ Login ------------------------------
const Login = ({ onSuccess }) => {
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/auth/login`, { password });
      localStorage.setItem("wntt_admin", data.token);
      onSuccess(data.token);
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : "Login failed. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-ink flex items-center justify-center px-6" data-testid="admin-login">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <img src="/assets/img/logo.png" alt="WE NEED TO TALK" className="h-20 w-20 rounded-full mx-auto ring-1 ring-gold/40 object-cover" />
          <p className="font-display text-lg tracking-[0.3em] text-ivory mt-5">CONTENT STUDIO</p>
          <p className="text-[10px] tracking-[0.28em] uppercase text-smoke mt-2">WE NEED TO TALK — private area</p>
        </div>
        <form onSubmit={submit} className={cardCls}>
          <label className={labelCls}>Password</label>
          <div className="relative">
            <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-smoke" />
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={inputCls + " pl-9"}
              placeholder="Your studio password"
              autoFocus
              data-testid="admin-password-input"
            />
          </div>
          {error && (
            <p className="text-red-400 text-xs mt-3" data-testid="admin-login-error">{error}</p>
          )}
          <button
            type="submit"
            disabled={loading || !password}
            className="mt-5 w-full inline-flex items-center justify-center gap-2 bg-gold hover:bg-gold-light disabled:opacity-50 text-ink text-[11px] font-semibold tracking-[0.24em] uppercase px-6 py-3.5 rounded-full transition-colors duration-300"
            data-testid="admin-login-button"
          >
            {loading ? <Loader2 size={13} className="animate-spin" /> : null}
            Enter the studio
          </button>
        </form>
      </div>
    </div>
  );
};

// ------------------------------ Media picker ------------------------------
const MediaPicker = ({ media, kind, onSelect, onClose }) => {
  const list = media.filter((m) => !kind || kind === "any" || m.kind === kind);
  return (
    <div className="fixed inset-0 z-50 bg-ink/80 backdrop-blur-sm flex items-center justify-center p-6" data-testid="media-picker">
      <div className="w-full max-w-3xl max-h-[80vh] overflow-y-auto bg-coal border border-white/10 rounded-xl p-6">
        <div className="flex items-center justify-between mb-5">
          <p className="font-display text-lg text-ivory">Media library</p>
          <button onClick={onClose} className="text-smoke hover:text-ivory" data-testid="media-picker-close"><X size={18} /></button>
        </div>
        {list.length === 0 && <p className="text-smoke text-sm">Nothing uploaded yet — use the Media tab to upload.</p>}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {list.map((m) => (
            <button
              key={m.id}
              onClick={() => onSelect(m.url)}
              className="group text-left border border-white/10 hover:border-gold/60 rounded-lg overflow-hidden transition-colors"
              data-testid={`media-pick-${m.id}`}
            >
              <div className="aspect-square bg-ink flex items-center justify-center overflow-hidden">
                {m.kind === "image" ? (
                  <img src={m.url} alt={m.filename} className="w-full h-full object-cover" />
                ) : m.kind === "video" ? (
                  <Film size={28} className="text-gold-light" />
                ) : (
                  <Music size={28} className="text-gold-light" />
                )}
              </div>
              <p className="text-[10px] text-smoke truncate px-2.5 py-2">{m.filename}</p>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

// ------------------------------ Studio ------------------------------
const TABS = [
  { id: "episodes", label: "Episodes" },
  { id: "text", label: "Text & Sections" },
  { id: "media", label: "Media Library" },
];

const Studio = ({ token, onLogout }) => {
  const [tab, setTab] = useState("episodes");
  const [doc, setDoc] = useState(null);
  const [media, setMedia] = useState([]);
  const [saving, setSaving] = useState(false);
  const [picker, setPicker] = useState(null); // {kind, onSelect}
  const headers = { "X-Admin-Token": token };

  const reloadMedia = () =>
    axios.get(`${API}/media/list`, { headers }).then(({ data }) => setMedia(data.media)).catch(() => {});

  useEffect(() => {
    axios
      .get(`${API}/content`)
      .then(({ data }) => setDoc(data.content || defaultSiteDoc()))
      .catch(() => setDoc(defaultSiteDoc()));
    reloadMedia();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setPath = (path, value) =>
    setDoc((d) => {
      const copy = JSON.parse(JSON.stringify(d));
      const keys = path.split(".");
      let o = copy;
      for (let i = 0; i < keys.length - 1; i++) o = o[keys[i]];
      o[keys[keys.length - 1]] = value;
      return copy;
    });

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/content`, doc, { headers });
      toast.success("Published — your changes are live right now.");
    } catch (e) {
      if (e.response?.status === 401) onLogout();
      else toast.error("Save failed. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const openPicker = (kind, onSelect) => setPicker({ kind, onSelect });

  if (!doc) {
    return (
      <div className="min-h-screen bg-ink flex items-center justify-center">
        <Loader2 className="animate-spin text-gold" size={28} />
      </div>
    );
  }

  const moveItem = (arr, i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= arr.length) return arr;
    const copy = [...arr];
    [copy[i], copy[j]] = [copy[j], copy[i]];
    return copy;
  };

  return (
    <div className="min-h-screen bg-ink text-ivory" data-testid="admin-studio">
      <header className="sticky top-0 z-40 bg-ink/90 backdrop-blur border-b border-white/10">
        <div className="max-w-5xl mx-auto px-5 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <img src="/assets/img/logo.png" alt="" className="h-9 w-9 rounded-full ring-1 ring-gold/40 object-cover" />
            <div>
              <p className="font-display text-sm tracking-[0.24em]">CONTENT STUDIO</p>
              <p className="text-[9px] tracking-[0.24em] uppercase text-smoke">edits publish instantly</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <a href="/" target="_blank" rel="noopener noreferrer" className="text-[10px] tracking-[0.2em] uppercase text-smoke hover:text-gold-light transition-colors" data-testid="admin-view-site">
              View site
            </a>
            <button
              onClick={save}
              disabled={saving}
              className="inline-flex items-center gap-2 bg-gold hover:bg-gold-light disabled:opacity-50 text-ink text-[10px] font-semibold tracking-[0.2em] uppercase px-5 py-2.5 rounded-full transition-colors duration-300"
              data-testid="admin-save-button"
            >
              {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
              Publish
            </button>
            <button onClick={onLogout} className="text-smoke hover:text-ivory transition-colors" aria-label="Log out" data-testid="admin-logout-button">
              <LogOut size={16} />
            </button>
          </div>
        </div>
        <div className="max-w-5xl mx-auto px-5 flex gap-1 pb-3">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`text-[10px] tracking-[0.2em] uppercase px-4 py-2 rounded-full transition-colors duration-300 ${
                tab === t.id ? "bg-gold text-ink font-semibold" : "text-smoke hover:text-ivory"
              }`}
              data-testid={`admin-tab-${t.id}`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-5 py-8 pb-24">
        {tab === "episodes" && <EpisodesTab doc={doc} setDoc={setDoc} setPath={setPath} openPicker={openPicker} moveItem={moveItem} />}
        {tab === "text" && <TextTab doc={doc} setPath={setPath} setDoc={setDoc} openPicker={openPicker} moveItem={moveItem} />}
        {tab === "media" && <MediaTab media={media} reloadMedia={reloadMedia} headers={headers} />}
      </main>

      {picker && (
        <MediaPicker
          media={media}
          kind={picker.kind}
          onSelect={(url) => { picker.onSelect(url); setPicker(null); }}
          onClose={() => setPicker(null)}
        />
      )}
    </div>
  );
};

// ------------------------------ Episodes tab ------------------------------
const EpisodesTab = ({ doc, setDoc, setPath, openPicker, moveItem }) => {
  const [open, setOpen] = useState(null);

  const updateEp = (i, key, value) =>
    setDoc((d) => {
      const copy = JSON.parse(JSON.stringify(d));
      copy.episodes[i][key] = value;
      return copy;
    });

  const addEpisode = () =>
    setDoc((d) => {
      const copy = JSON.parse(JSON.stringify(d));
      const nextNum = Math.max(0, ...copy.episodes.map((e) => e.num)) + 1;
      copy.episodes.push({ num: nextNum, title: "NEW EPISODE TITLE", desc: "", img: "", videoId: null, teaser: null, hidden: true });
      return copy;
    });

  return (
    <div className="space-y-4" data-testid="admin-episodes-tab">
      <Field
        label="Latest episode — synopsis shown in the big feature section"
        textarea
        value={doc.latestSynopsis}
        onChange={(v) => setPath("latestSynopsis", v)}
        testid="admin-latest-synopsis"
      />
      {doc.episodes.map((ep, i) => (
        <div key={i} className={cardCls} data-testid={`admin-episode-${ep.num}`}>
          <button onClick={() => setOpen(open === i ? null : i)} className="w-full flex items-center justify-between gap-3 text-left" data-testid={`admin-episode-toggle-${ep.num}`}>
            <div className="flex items-center gap-3 min-w-0">
              <span className="text-gold font-display text-sm shrink-0">EP {String(ep.num).padStart(2, "0")}</span>
              <span className="text-sm text-ivory truncate">{ep.title}</span>
              {ep.videoId && <span className="shrink-0 text-[9px] tracking-[0.18em] uppercase bg-gold/15 text-gold-light px-2 py-0.5 rounded-full">Live</span>}
              {ep.hidden && <span className="shrink-0 text-[9px] tracking-[0.18em] uppercase bg-white/10 text-smoke px-2 py-0.5 rounded-full">Hidden</span>}
              {doc.latestEpisodeNum === ep.num && <span className="shrink-0 text-[9px] tracking-[0.18em] uppercase border border-gold/40 text-gold-light px-2 py-0.5 rounded-full">Featured</span>}
            </div>
            <span className="text-smoke text-xs shrink-0">{open === i ? "Close" : "Edit"}</span>
          </button>

          {open === i && (
            <div className="mt-5 pt-5 border-t border-white/10 space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <Field label="Episode number" value={ep.num} onChange={(v) => updateEp(i, "num", Number(v) || ep.num)} testid={`ep-${i}-num`} />
                <Field label="Title" value={ep.title} onChange={(v) => updateEp(i, "title", v)} testid={`ep-${i}-title`} />
              </div>
              <Field label="Short description" textarea value={ep.desc} onChange={(v) => updateEp(i, "desc", v)} testid={`ep-${i}-desc`} />
              <div>
                <label className={labelCls}>Thumbnail image</label>
                <div className="flex gap-2">
                  <input value={ep.img ?? ""} onChange={(e) => updateEp(i, "img", e.target.value)} className={inputCls} placeholder="/assets/img/ep1.jpg or pick from library" data-testid={`ep-${i}-img`} />
                  <PickButton kind="image" onPick={(k) => openPicker(k, (url) => updateEp(i, "img", url))} />
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <Field label="YouTube video ID (when live)" value={ep.videoId ?? ""} onChange={(v) => updateEp(i, "videoId", v.trim() || null)} placeholder="e.g. 47oD9IQZ5as — leave empty before release" testid={`ep-${i}-videoid`} />
                <Field label="Premiere date/time (for countdown)" value={(ep.premiere || "").slice(0, 16)} onChange={(v) => updateEp(i, "premiere", v || undefined)} placeholder="2026-08-24T12:00" testid={`ep-${i}-premiere`} />
              </div>
              <div>
                <label className={labelCls}>Teaser video (plays until the episode is live)</label>
                <div className="flex gap-2">
                  <input value={ep.teaser ?? ""} onChange={(e) => updateEp(i, "teaser", e.target.value || null)} className={inputCls} placeholder="/assets/video/teaser-ep1.mp4 or upload one" data-testid={`ep-${i}-teaser`} />
                  <PickButton kind="video" onPick={(k) => openPicker(k, (url) => updateEp(i, "teaser", url))} />
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-5 pt-1">
                <label className="flex items-center gap-2 text-xs text-smoke cursor-pointer" data-testid={`ep-${i}-hidden-label`}>
                  <input type="checkbox" checked={!!ep.hidden} onChange={(e) => updateEp(i, "hidden", e.target.checked)} className="accent-[#B8860B]" data-testid={`ep-${i}-hidden`} />
                  Hidden from site
                </label>
                <label className="flex items-center gap-2 text-xs text-smoke cursor-pointer" data-testid={`ep-${i}-latest-label`}>
                  <input type="radio" name="latestEp" checked={doc.latestEpisodeNum === ep.num} onChange={() => setPath("latestEpisodeNum", ep.num)} className="accent-[#B8860B]" data-testid={`ep-${i}-latest`} />
                  Feature as latest
                </label>
                <div className="flex items-center gap-1.5 ml-auto">
                  <button onClick={() => setPath("episodes", moveItem(doc.episodes, i, -1))} className="p-2 text-smoke hover:text-ivory" aria-label="Move up" data-testid={`ep-${i}-up`}><ArrowUp size={14} /></button>
                  <button onClick={() => setPath("episodes", moveItem(doc.episodes, i, 1))} className="p-2 text-smoke hover:text-ivory" aria-label="Move down" data-testid={`ep-${i}-down`}><ArrowDown size={14} /></button>
                  <button
                    onClick={() => setPath("episodes", doc.episodes.filter((_, j) => j !== i))}
                    className="p-2 text-smoke hover:text-red-400"
                    aria-label="Delete episode"
                    data-testid={`ep-${i}-delete`}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      ))}
      <button
        onClick={addEpisode}
        className="w-full inline-flex items-center justify-center gap-2 border border-dashed border-gold/40 hover:border-gold text-gold-light text-[11px] tracking-[0.22em] uppercase px-6 py-4 rounded-lg transition-colors duration-300"
        data-testid="admin-add-episode"
      >
        <Plus size={13} /> Add episode
      </button>
    </div>
  );
};

// ------------------------------ Text & sections tab ------------------------------
const TextTab = ({ doc, setPath, setDoc, openPicker, moveItem }) => {
  const lines = (arr) => (arr || []).join("\n");
  const setLines = (path) => (v) => setPath(path, v.split("\n").map((s) => s.trim()).filter(Boolean));
  const paras = (arr) => (arr || []).join("\n\n");
  const setParas = (path) => (v) => setPath(path, v.split(/\n\s*\n/).map((s) => s.trim()).filter(Boolean));

  const Group = ({ title, children }) => (
    <div className={cardCls + " space-y-4"}>
      <p className="font-display text-sm tracking-[0.2em] uppercase text-gold-light">{title}</p>
      {children}
    </div>
  );

  return (
    <div className="space-y-5" data-testid="admin-text-tab">
      <div className={cardCls}>
        <p className="font-display text-sm tracking-[0.2em] uppercase text-gold-light mb-4">Page sections — rename, reorder, show/hide</p>
        <div className="space-y-2">
          {doc.sections.map((s, i) => (
            <div key={s.id} className="flex items-center gap-3" data-testid={`admin-section-${s.id}`}>
              <button onClick={() => setPath("sections", moveItem(doc.sections, i, -1))} className="p-1.5 text-smoke hover:text-ivory" aria-label="Move up"><ArrowUp size={13} /></button>
              <button onClick={() => setPath("sections", moveItem(doc.sections, i, 1))} className="p-1.5 text-smoke hover:text-ivory" aria-label="Move down"><ArrowDown size={13} /></button>
              <input value={s.label} onChange={(e) => setPath(`sections.${i}.label`, e.target.value)} className={inputCls + " flex-1"} data-testid={`section-label-${s.id}`} />
              <button
                onClick={() => setPath(`sections.${i}.visible`, s.visible === false)}
                className={`p-1.5 transition-colors ${s.visible === false ? "text-smoke" : "text-gold-light"}`}
                aria-label="Toggle visibility"
                data-testid={`section-visible-${s.id}`}
              >
                {s.visible === false ? <EyeOff size={15} /> : <Eye size={15} />}
              </button>
            </div>
          ))}
        </div>
      </div>

      <Group title="Hero (opening screen)">
        <div>
          <label className={labelCls}>Background image</label>
          <div className="flex gap-2">
            <input value={doc.hero.bg} onChange={(e) => setPath("hero.bg", e.target.value)} className={inputCls} data-testid="hero-bg-input" />
            <PickButton kind="image" onPick={(k) => openPicker(k, (url) => setPath("hero.bg", url))} />
          </div>
        </div>
        <Field label="Overline (small text above title)" value={doc.hero.overline} onChange={(v) => setPath("hero.overline", v)} testid="hero-overline-input" />
        <Field label="Sub-headline (italic quote)" value={doc.hero.subheadline} onChange={(v) => setPath("hero.subheadline", v)} testid="hero-subheadline-input" />
        <Field label="Supporting text" textarea value={doc.hero.supporting} onChange={(v) => setPath("hero.supporting", v)} testid="hero-supporting-input" />
        <Field label="Second button label" value={doc.hero.familyLabel} onChange={(v) => setPath("hero.familyLabel", v)} testid="hero-familylabel-input" />
      </Group>

      <Group title="The Story">
        <div>
          <label className={labelCls}>Side image</label>
          <div className="flex gap-2">
            <input value={doc.story.image} onChange={(e) => setPath("story.image", e.target.value)} className={inputCls} data-testid="story-image-input" />
            <PickButton kind="image" onPick={(k) => openPicker(k, (url) => setPath("story.image", url))} />
          </div>
        </div>
        <Field label="Image caption" value={doc.story.caption} onChange={(v) => setPath("story.caption", v)} />
        <Field label="Paragraphs (blank line between each)" textarea rows={6} value={paras(doc.story.paras)} onChange={setParas("story.paras")} testid="story-paras-input" />
      </Group>

      <Group title="Family / Episodes intros">
        <Field label="Family section side text" value={doc.family.side} onChange={(v) => setPath("family.side", v)} />
        <Field label="Episodes section side text" value={doc.episodesSide} onChange={(v) => setPath("episodesSide", v)} />
      </Group>

      <Group title="The Sound (theme player)">
        <Field label="Tagline" value={doc.sound.tagline} onChange={(v) => setPath("sound.tagline", v)} />
        <Field label="Track title" value={doc.sound.trackTitle} onChange={(v) => setPath("sound.trackTitle", v)} />
        <div>
          <label className={labelCls}>Audio file</label>
          <div className="flex gap-2">
            <input value={doc.sound.trackSrc} onChange={(e) => setPath("sound.trackSrc", e.target.value)} className={inputCls} data-testid="sound-src-input" />
            <PickButton kind="audio" onPick={(k) => openPicker(k, (url) => setPath("sound.trackSrc", url))} />
          </div>
        </div>
      </Group>

      <Group title="Lessons & Quotes">
        <Field label="Lessons (one per line)" textarea rows={5} value={lines(doc.lessons)} onChange={setLines("lessons")} testid="lessons-input" />
        <Field label="Lesson hover line" value={doc.lessonsHover} onChange={(v) => setPath("lessonsHover", v)} />
        <Field label="Rotating quotes (one per line)" textarea rows={6} value={lines(doc.quotes)} onChange={setLines("quotes")} testid="quotes-input" />
      </Group>

      <Group title="Watch / Newsletter">
        <Field label="Watch section tagline" value={doc.watch.tagline} onChange={(v) => setPath("watch.tagline", v)} />
        <Field label="Newsletter overline" value={doc.newsletter.overline} onChange={(v) => setPath("newsletter.overline", v)} />
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Newsletter heading" value={doc.newsletter.headingMain} onChange={(v) => setPath("newsletter.headingMain", v)} />
          <Field label="Heading gold accent word" value={doc.newsletter.headingAccent} onChange={(v) => setPath("newsletter.headingAccent", v)} />
        </div>
        <Field label="Newsletter body" textarea value={doc.newsletter.body} onChange={(v) => setPath("newsletter.body", v)} />
      </Group>

      <Group title="Creator">
        <Field label="About text" textarea value={doc.creator.text} onChange={(v) => setPath("creator.text", v)} />
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Created & Produced by" value={doc.creator.producedBy} onChange={(v) => setPath("creator.producedBy", v)} />
          <Field label="Written by" value={doc.creator.writtenBy} onChange={(v) => setPath("creator.writtenBy", v)} />
        </div>
        <div>
          <label className={labelCls}>Portrait image</label>
          <div className="flex gap-2">
            <input value={doc.creator.image} onChange={(e) => setPath("creator.image", e.target.value)} className={inputCls} data-testid="creator-image-input" />
            <PickButton kind="image" onPick={(k) => openPicker(k, (url) => setPath("creator.image", url))} />
          </div>
        </div>
      </Group>

      <Group title="Marquee strip & Social links">
        <Field label="Marquee items (one per line)" textarea rows={5} value={lines(doc.marquee)} onChange={setLines("marquee")} testid="marquee-input" />
        {["youtube", "tiktok", "instagram", "facebook"].map((k) => (
          <Field key={k} label={k.charAt(0).toUpperCase() + k.slice(1) + " URL"} value={doc.socials[k]} onChange={(v) => setPath(`socials.${k}`, v)} testid={`social-${k}-input`} />
        ))}
      </Group>
    </div>
  );
};

// ------------------------------ Media tab ------------------------------
const MediaTab = ({ media, reloadMedia, headers }) => {
  const [uploading, setUploading] = useState(false);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      await axios.post(`${API}/media/upload`, form, { headers, timeout: 600000 });
      toast.success(`"${file.name}" uploaded — pick it anywhere with the Library button.`);
      reloadMedia();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Upload failed. Try a smaller file.");
    } finally {
      setUploading(false);
    }
  };

  const del = async (m) => {
    try {
      await axios.delete(`${API}/media/${m.id}`, { headers });
      toast.success("Removed from library.");
      reloadMedia();
    } catch {
      toast.error("Delete failed.");
    }
  };

  const copyUrl = (m) => {
    navigator.clipboard?.writeText(`${window.location.origin}${m.url}`).catch(() => {});
    toast.success("Link copied to clipboard.");
  };

  const fmtSize = (b) => (b > 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.round(b / 1024)} KB`);

  return (
    <div data-testid="admin-media-tab">
      <label
        className={`w-full inline-flex items-center justify-center gap-3 border border-dashed border-gold/40 hover:border-gold text-gold-light text-[11px] tracking-[0.22em] uppercase px-6 py-8 rounded-lg transition-colors duration-300 cursor-pointer ${uploading ? "opacity-50 pointer-events-none" : ""}`}
        data-testid="media-upload-dropzone"
      >
        {uploading ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
        {uploading ? "Uploading — keep this tab open…" : "Upload image, video or audio (any size, any ratio)"}
        <input type="file" accept="image/*,video/*,audio/*" className="hidden" onChange={upload} data-testid="media-upload-input" />
      </label>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
        {media.map((m) => (
          <div key={m.id} className="border border-white/10 rounded-lg overflow-hidden bg-coal/60" data-testid={`media-item-${m.id}`}>
            <div className="aspect-square bg-ink flex items-center justify-center overflow-hidden">
              {m.kind === "image" ? (
                <img src={m.url} alt={m.filename} className="w-full h-full object-cover" />
              ) : m.kind === "video" ? (
                <video src={m.url} className="w-full h-full object-cover" muted preload="metadata" />
              ) : (
                <Music size={28} className="text-gold-light" />
              )}
            </div>
            <div className="p-2.5">
              <p className="text-[10px] text-ivory truncate">{m.filename}</p>
              <p className="text-[9px] text-smoke mt-0.5">{m.kind} • {fmtSize(m.size)}</p>
              <div className="flex gap-1.5 mt-2">
                <button onClick={() => copyUrl(m)} className="flex-1 text-[9px] tracking-[0.14em] uppercase border border-white/15 hover:border-gold/50 text-smoke hover:text-gold-light rounded px-2 py-1.5 transition-colors" data-testid={`media-copy-${m.id}`}>
                  Copy link
                </button>
                <button onClick={() => del(m)} className="text-[9px] tracking-[0.14em] uppercase border border-white/15 hover:border-red-400/60 text-smoke hover:text-red-400 rounded px-2 py-1.5 transition-colors" data-testid={`media-delete-${m.id}`}>
                  <Trash2 size={11} />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
      {media.length === 0 && (
        <p className="text-center text-smoke text-sm mt-10">Your library is empty. Uploaded media lives here and can be attached to episodes, the hero, the story — anywhere with a Library button.</p>
      )}
    </div>
  );
};

// ------------------------------ Root ------------------------------
export default function Admin() {
  const [token, setToken] = useState(localStorage.getItem("wntt_admin") || "");
  const [checking, setChecking] = useState(!!token);

  useEffect(() => {
    if (!token) return setChecking(false);
    axios
      .get(`${API}/auth/check`, { headers: { "X-Admin-Token": token } })
      .then(() => setChecking(false))
      .catch(() => {
        localStorage.removeItem("wntt_admin");
        setToken("");
        setChecking(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { headers: { "X-Admin-Token": token } });
    } catch {}
    localStorage.removeItem("wntt_admin");
    setToken("");
  };

  return (
    <div className="App grain">
      {checking ? (
        <div className="min-h-screen bg-ink flex items-center justify-center">
          <Loader2 className="animate-spin text-gold" size={28} />
        </div>
      ) : token ? (
        <Studio token={token} onLogout={logout} />
      ) : (
        <Login onSuccess={setToken} />
      )}
      <Toaster theme="dark" position="bottom-center" />
    </div>
  );
}
